import { Location } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
    AuctionCatalogsService,
    CreateAdminInvoice201Response,
    InvoiceDetail,
    InvoiceItem,
    InvoicePayment,
    InvoiceStatus,
    InvoiceStatusType,
    PaymentType,
    PaymentProfileCardsResponse,
    ListCards200Response,
    ShippingQuoteStatus,
} from '@viewbid/ts-angular-client';
import { LimitCharacters } from 'viewbid-shared-lib';

import { InvoiceStatus as InvoiceStatuses } from '../../enums/invoice-statuses';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PaymentErrorCodes } from '../../enums/payment-error-codes';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { CardTypes } from '../../enums/card-types';
import { InvoicesErrorCodes } from '../../enums/invoices-error-codes';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import { ChatBoxService } from 'src/app/viewbid/shared/components/chatbox/service/chatbox.service';
import { isCardExpired } from 'src/app/viewbid/shared/utils/payment-utils';
import { invoiceStatusLabel } from '../../utils/invoice-status-label.util';
import { VbButtonVariant, VbSnackbarService } from '@viewbid/ui';

@Component({
    selector: 'app-view-invoice-page',
    templateUrl: './view-invoice-page.component.html',
    styleUrls: ['./view-invoice-page.component.scss'],
})
export class ViewInvoicePageComponent implements OnInit, OnDestroy {
    isLoading: boolean = false;
    invoiceId: string = '';
    invoiceDetails!: InvoiceDetail;
    showInvoiceHistory: boolean = false;
    invoiceHistory: any = [];
    shippingTotal: number = 0;
    showShippingQuote: boolean = false;
    isApproveShippingQuote: boolean = false;
    isPayOnPickup: boolean = false;
    selectedPaymentCard: PaymentProfileCardsResponse | null = null;
    private successToastId: number = 0;
    paymentSuccess: boolean = false;
    paymentMethods: Array<PaymentProfileCardsResponse> = [];
    isShowDetails: boolean = false;
    trackingInfo: any;
    private mobile$ = new Subject<void>();
    public isMobile: boolean = false;
    isDownloading: boolean = false;
    isPrinting: boolean = false;
    private printIframe?: HTMLIFrameElement;

    constructor(
        private auctionCatalogsService: AuctionCatalogsService,
        private route: ActivatedRoute,
        private location: Location,
        public limitChars: LimitCharacters,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private router: Router,
        private deviceCheckService: DeviceCheckService,
        private chatBoxService: ChatBoxService,
    ) {}
    ngOnInit(): void {
        this.route.params.subscribe((params: Params) => {
            this.invoiceId = params['id'];
        });
        this.getInvoice();
        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.mobile$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
    }
    ngOnDestroy(): void {
        localStorage.removeItem('invoiceShippingPanel');
        this.printIframe?.remove();
        this.mobile$.next();
        this.mobile$.complete();
    }

    public get invoiceStatusType(): typeof InvoiceStatuses {
        return InvoiceStatuses;
    }

    private getInvoice(): void {
        this.isLoading = true;
        this.auctionCatalogsService
            .getUserInvoiceById(this.invoiceId)
            .subscribe({
                next: (response: CreateAdminInvoice201Response) => {
                    this.invoiceDetails = {
                        ...response.data,
                        items: response.data.items.sort(
                            (
                                { number: numberA }: any,
                                { number: numberB }: any,
                            ) => {
                                if (numberA) {
                                    return numberB ? numberA - numberB : -1;
                                }
                                return 1;
                            },
                        ),
                    };
                    this.invoiceHistory = this.combineAndSortInvoices(
                        this.invoiceDetails.invoiceStatuses,
                        this.invoiceDetails.invoicePayments,
                    );
                    if (this.invoiceDetails.shipping?.isApproved) {
                        this.calculateShippingAmount();
                    }
                    if (this.invoiceDetails.trackingNumbers.length) {
                        this.trackingInfo = {
                            number: this.invoiceDetails.trackingNumbers[0]
                                .number,
                            method: this.invoiceDetails.shipping?.method,
                            company: this.invoiceDetails.vendor?.name,
                        };
                    }
                    this.showShippingQuote =
                        localStorage.getItem('invoiceShippingPanel') === 'true'
                            ? true
                            : false;
                    this.getPaymentMethods();
                },
                error: (error: HttpErrorResponse) => {
                    this.isLoading = false;
                    const errorMessage: string =
                        error.error.errorCode ===
                        InvoicesErrorCodes.INVOICE_NOT_FOUND_ERROR
                            ? this.translate.instant(
                                  'Invoices.invoiceNotFoundError',
                              )
                            : error.error.message;

                    this.snackbarService.error(errorMessage);
                    this.router.navigate(['profiles/my-profile/my-invoices']);
                },
            });
    }

    goBack(): void {
        this.location.back();
    }

    /** Breadcrumb trail — invoice number lands once the fetch resolves. */
    get crumbTrail(): string[] {
        const number: number | undefined = this.invoiceDetails?.invoice?.number;
        return ['My invoices', number ? `Invoice #${number}` : 'Invoice'];
    }

    onCrumbSelect(index: number): void {
        if (index === 0) {
            this.router.navigate(['profiles/my-profile/my-invoices']);
        }
    }

    /** Header CTA mirroring the invoices table's action button (same
     *  first-match-wins conditions and button variants). The shipping-review
     *  state is excluded: on this page that action is the quote panel toggle
     *  sitting right beside it. View-only states get no CTA. */
    get headerAction(): { label: string; variant: VbButtonVariant } | null {
        const statusType: InvoiceStatusType | undefined =
            this.invoiceDetails?.currentStatus?.statusType;
        if (statusType === InvoiceStatuses.PendingBuyerReview) {
            return { label: 'Finalize invoice', variant: 'secondary' };
        }
        if (statusType === InvoiceStatuses.PendingBuyerShippingReview) {
            return null;
        }
        if ((this.invoiceDetails?.invoice?.amountDue ?? 0) > 0) {
            return { label: 'Pay invoice', variant: 'primary' };
        }
        return null;
    }

    /** Same destination as the table's Finalize/Pay actions. */
    goToInvoiceActions(): void {
        this.router.navigate([
            '/invoice-actions',
            {
                invoiceNo: this.invoiceDetails.invoice?.number,
                id: this.invoiceId,
            },
        ]);
    }

    calculateTotalItemAmount(invoiceItem: InvoiceItem | null): number {
        if (!invoiceItem) {
            return 0;
        }

        const { price = 0, quantity = 0 } = invoiceItem;

        return Number(price) * Number(quantity);
    }

    toggleInvoiceHistory(): void {
        this.showShippingQuote = false;
        this.showInvoiceHistory = !this.showInvoiceHistory;
    }

    /** Plain-language history labels — the same vocabulary the invoices
     *  table's chips use, never the raw workflow status string. */
    statusLabel(statusType?: string): string {
        return invoiceStatusLabel(statusType);
    }

    onToggleShippingQuote(): void {
        this.showInvoiceHistory = false;
        this.showShippingQuote = !this.showShippingQuote;
    }

    private combineAndSortInvoices(
        statuses: InvoiceStatus[],
        payments: InvoicePayment[],
    ): any {
        const combined: any = [
            ...statuses.map((status: InvoiceStatus) => ({
                ...status,
                type: 'status',
                sortDate: new Date(status.createdOn),
            })),
            ...payments.map((payment: InvoicePayment) => ({
                ...payment,
                type: 'payment',
                sortDate: new Date(payment.createdOn),
            })),
        ];

        return combined.sort(
            (
                a: { sortDate: { getTime: () => number } },
                b: { sortDate: { getTime: () => number } },
            ) => a.sortDate.getTime() - b.sortDate.getTime(),
        );
    }

    calculateShippingAmount(): void {
        this.shippingTotal = this.invoiceDetails.shipping?.subtotal ?? 0;
    }

    private async fetchInvoicePdf(): Promise<Blob | null> {
        try {
            return await firstValueFrom(
                this.auctionCatalogsService.downloadInvoicePdf(this.invoiceId),
            );
        } catch {
            this.snackbarService.error(
                this.translate.instant('Invoices.downloadInvoiceError'),
            );
            return null;
        }
    }

    async downloadInvoice(): Promise<void> {
        if (this.isDownloading || this.isPrinting) {
            return;
        }
        this.isDownloading = true;
        try {
            const blob: Blob | null = await this.fetchInvoicePdf();
            if (!blob) {
                return;
            }
            const url: string = URL.createObjectURL(blob);
            const link: HTMLAnchorElement = document.createElement('a');
            link.href = url;
            link.download = `invoice_${this.invoiceDetails.invoice?.number ?? this.invoiceId}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } finally {
            this.isDownloading = false;
        }
    }

    async printInvoice(): Promise<void> {
        if (this.isDownloading || this.isPrinting) {
            return;
        }
        this.isPrinting = true;
        const blob: Blob | null = await this.fetchInvoicePdf();
        if (!blob) {
            this.isPrinting = false;
            return;
        }
        if (!this.printIframe) {
            this.printIframe = document.createElement('iframe');
            this.printIframe.style.cssText =
                'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
            document.body.appendChild(this.printIframe);
        }
        const iframe: HTMLIFrameElement = this.printIframe;
        const url: string = URL.createObjectURL(blob);
        iframe.onload = (): void => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            this.isPrinting = false;
            // Firefox needs the blob URL alive while the print dialog is open.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        };
        iframe.src = url;
    }

    getShippingTaxAmount(): number {
        return this.shippingTotal * 0.15;
    }

    onShowDetails($event: any): void {
        this.isShowDetails = $event.target.checked;
    }

    onApproveShippingQuote(): void {
        this.isApproveShippingQuote = true;
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }

    onCancelShippingQuote(): void {
        this.selectedPaymentCard = null;
        this.isApproveShippingQuote = false;
    }

    private async getPaymentMethods(): Promise<void> {
        await this.auctionCatalogsService.listCards().subscribe({
            next: (response: ListCards200Response) => {
                this.isLoading = false;
                this.paymentMethods = response?.data;
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant('Payments.cardListLoadFailed'),
                );
            },
        });
    }

    onPayNow(): void {
        this.makePayment();
    }

    isCardExpired(expirationDate?: string): boolean {
        return isCardExpired(expirationDate);
    }

    private async makePayment(): Promise<void> {
        if (isCardExpired(this.selectedPaymentCard?.expirationDate)) {
            this.snackbarService.error(
                this.translate.instant('Payments.cardExpired'),
            );
            return;
        }
        this.isLoading = true;
        await this.auctionCatalogsService
            .createPayment(this.invoiceId, {
                invoicesId: this.invoiceId,
                paymentTypes: PaymentType.OnlineCard,
                amountPaid: this.invoiceDetails?.invoice?.amountDue ?? 0,
                customerPaymentProfileId:
                    this.selectedPaymentCard?.customerPaymentProfileId ?? '',
            })
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Invoices.shippingConfirmedSuccess',
                        ),
                    );
                    this.paymentSuccess = true;
                    this.completeApproveShippingQuote();
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage: string =
                        error.error.errorCode ===
                            PaymentErrorCodes.AVS_MISMATCH &&
                        error.error.details?.errors?.length > 0
                            ? error.error.details.errors[0]
                            : error.error.message;

                    this.snackbarService.error(errorMessage);
                    this.isLoading = false;
                },
            });
    }

    onSelectPaymentMethod(i: any): void {
        this.selectedPaymentCard = this.paymentMethods[i];
    }

    completeApproveShippingQuote(): void {
        const shippingId: string | undefined = this.invoiceDetails.shipping?.id;
        if (!shippingId) {
            return;
        }
        this.auctionCatalogsService
            .userApproveShippingQuote(shippingId, ShippingQuoteStatus.Approve)
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Invoices.shippingQuoteApproved',
                        ),
                    );
                    this.isLoading = false;
                    localStorage.removeItem('invoiceShippingPanel');
                    this.getInvoice();
                },
                error: (error: HttpErrorResponse) => {
                    const errorMessage: string =
                        error.error.errorCode ===
                            PaymentErrorCodes.AVS_MISMATCH &&
                        error.error.details?.errors?.length > 0
                            ? error.error.details.errors[0]
                            : error.error.message;
                    this.snackbarService.error(errorMessage);
                    this.isLoading = false;
                },
            });
    }

    getCardTypeClass(cardType: any): { [key: string]: boolean } {
        return {
            'fa-cc-visa': cardType === CardTypes.Visa,
            'fa-cc-mastercard': cardType === CardTypes.MasterCard,
            'fa-cc-amex': cardType === CardTypes.Amex,
            'fa-cc-stripe': cardType === CardTypes.Stripe,
            'fa-cc-jcb': cardType === CardTypes.JCB,
            'fa-cc-discover': cardType === CardTypes.Discover,
            'fa-cc-diners-club': cardType === CardTypes.DinersClub,
            'fa-cc-apple-pay': cardType === CardTypes.ApplePay,
            'fa-cc-amazon-pay': cardType === CardTypes.AmazonPay,
            'fa-cc-paypal': cardType === CardTypes.Paypal,
            'fa-credit-card': cardType === '',
        };
    }

    getLotNumber(item: any): string {
        if (!item.number) {
            return item.price > 0 ? 'Debit' : 'Credit';
        }
        return `${item.number}${item.suffix ?? ''}`;
    }

    sendMessageToSeller(): void {
        this.chatBoxService.open();
    }
}
